<template>
   <v-container fluid>
        <v-row>
            <v-col>
                <h4 class="text-h4">Accounts Payable Summary Report</h4>
                <p class="text-subtitle-1">Summary of supplier accounts summary</p>
            </v-col>
        </v-row>
        <v-divider></v-divider>
        <v-form ref="form">
            <v-row class="mt-1">
                <v-col>
                    <DatePicker
                        label="As Of"
                        outlined
                        v-model="form.date"
                        dense
                        hide-details/>
                </v-col>
                <v-col>
                    <v-autocomplete
                        label="Supplier Group"
                        v-model="form.supplier_group_id"
                        outlined
                        dense
                        hide-details
                        :items="groups"
                        :loading="groupsLoading"
                        @update:search-input="searchGroups"
                        item-text="name"
                        item-value="id"
                        clearable
                    ></v-autocomplete>
                </v-col>
                <v-col>
                    <v-autocomplete
                        label="Currency"
                        v-model="form.currency_id"
                        :rules="[isRequired]"
                        outlined
                        dense
                        hide-details
                        :items="currencies"
                        item-text="abbreviation"
                        :loading="currenciesLoading"
                        @update:search-input="searchCurrencies"
                        item-value="id"
                        clearable
                    ></v-autocomplete>
                </v-col>
                <v-col>
                    <v-autocomplete
                        label="Balance Type"
                        v-model="form.balance_type"
                        :rules="[isRequired]"
                        outlined
                        dense
                        hide-details
                        :items="balanceTypes"
                        item-text="type"
                        item-value="id"
                        clearable
                    ></v-autocomplete>
                </v-col>
                <v-col>
                    <v-btn class="primary" block :loading="loading" @click="getAccountSummary">
                        <v-icon left>mdi-magnify</v-icon>
                        search
                    </v-btn>
                </v-col>
                <v-col>
                     <v-btn color="scoundary" @click="handlePrint">
                        <v-icon left>mdi-printer</v-icon>
                        print
                    </v-btn>
                </v-col>
            </v-row>
        </v-form>
        <v-row >
            <v-col>
                <v-text-field
                    label="filter"
                    v-model="filter"
                    prepend-inner-icon="mdi-filter"
                    shaped
                    filled
                    dense
                    single-line
                    hide-details>
                </v-text-field>
            </v-col>
        </v-row>
        <v-row no-gutters>
            <v-col>
                <v-progress-linear indeterminate v-if="loading" />
                <v-simple-table dense>
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Supplier</th>
                            <th>Account</th>
                            <th>Currency</th>
                            <th>Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(v, i) in filterSummary" :key="i" v-ripple @dblclick="handleStatement(v)">
                            <td>{{i+1}}</td>
                            <td>{{v.supplier_code}} {{v.supplier_name}}</td>
                            <td>{{v.account_name}}</td>
                            <td>{{v.currency_abbreviation}}</td>
                            <td>
                                <v-btn icon @click="handleStatement(v)">
                                    <v-icon>mdi-text-box-outline</v-icon>
                                </v-btn>
                                <span :class="{'error--text': isNegative(v.amount)}">
                                    {{formatMoney(v.amount, v.currency_abbreviation)}}
                                </span>
                            </td>
                        </tr>
                        <tr v-if="currency && currency.id != null">
                            <td></td>
                            <td></td>
                            <td>Total</td>
                            <td>{{currency.abbreviation}}</td>
                            <td>
                               <span :class="{'error--text': isNegative(summaryTotal)}">
                                    {{formatMoney(summaryTotal, currency.abbreviation)}}
                                </span>
                            </td>
                        </tr>
                    </tbody>
                </v-simple-table>
            </v-col>
        </v-row>
   </v-container>
</template>

<script>
import {accountSummary, printAccountSummary} from '../../services/purchase/reports';
import { formatMoney } from 'accounting';
import DatePicker from './../../components/common/DatePicker.vue';
import moment from 'moment';
import currencyAPI from './../../services/purchase/currency';
import groupAPI from './../../services/purchase/supplierGroup';
import { isRequired } from './../../utils/validations';
import { mapActions } from 'vuex';

export default {
    components: { DatePicker },
    data() {
        return {
            summary: [],
            summaryTotal: 0.0,
            groups: [
                { id: null, name: "All" }
            ],
            groupsLoading: false,
            currencies: [],
            currenciesLoading: false,
            loading: false,
            form: {
                date: moment().format("YYYY-MM-DD"),
                supplier_group_id: null,
                currency_id: 1,
                balance_type: 1
            },
            filter: "",
            balanceTypes: [
                { id: 1, type: 'Credit' },
                { id: 2, type: 'Debit'}
            ]
        }
    },
    computed: {
        filterSummary() {
            return this.summary.filter( each => {
                return each.supplier_name.toLowerCase().indexOf(this.filter.toLowerCase()) > -1 ||
                       each.account_name.toLowerCase().indexOf(this.filter.toLowerCase()) > -1
            })
        },
        currency() {
            return this.currencies.find(each => {
                return each.id == this.form.currency_id
            })
        }
    },
    methods: {
        isRequired,
        ...mapActions("purchase/supplierAccountStatement", {
            initSuppierAccountStatement: "init"
        }),
        formatMoney(n, currency) {
            return formatMoney(n, {
                symbol: currency,
                precision: 2,
                thousand: ",",
                format: {
                    pos : "%s %v",
                    neg : "%s (%v)",
                    zero: "--"
                }
            });
        },
        isNegative(n) {
            return Math.sign(n) == -1;
        },
        async getAccountSummary() {
            if(!this.$refs.form.validate()) {
                return;
            }

            try {
                this.loading = true;
                this.summary = [];
                this.summaryTotal = 0.0;
                let res = await accountSummary(this.form);
                this.summary = res.data.list;
                this.summaryTotal = res.data.total;
            } catch(error) {
                console.log(error);
            } finally {
                this.loading = false;
            }
        },
        searchCurrencies(query) {
            this.currenciesLoading = true;
            currencyAPI.search(query).then(response => {
                this.currencies = Array.from(new Set([...this.currencies, ...response.data]))
            }).finally(() => {
                this.currenciesLoading = false;
            })
        },
        searchGroups(query) {
            this.groupsLoading = true;
            groupAPI.search(query).then(response => {
                this.groups = Array.from(new Set([...this.groups, ...response.data]));
            }).finally(() => {
                this.groupsLoading = false;
            })
        },
        handleStatement({account_id, supplier_id}) {
            this.initSuppierAccountStatement({
                supplier_id,
                account_id,
                to: this.form.date,
                from: moment().startOf("year").format("YYYY-MM-DD")
            })
            this.$router.push({ name: "SupplierAccountStatementView"})
        },
         handlePrint() {
            printAccountSummary(this.form)
                .then(response => {
                    var printWindow = window.open('','','width=800,height=600');
                    printWindow.document.write(response.data);
                    printWindow.document.close();
                    printWindow.focus();
                    printWindow.print();
                    printWindow.close();
                })
                .catch(error => {
                    let message = error.response ? error.response.data.message : error;
                    this.snackbar = { value: true, color: "error", text: message }
                });
        },
    },
    mounted() {
        this.getAccountSummary();
    }
}

</script>